import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 10;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking'); // checking | paid | failed | timeout | missing
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('missing');
      return;
    }

    let attempts = 0;
    let cancelled = false;
    const apiUrl = process.env.REACT_APP_BACKEND_URL;

    const poll = async () => {
      try {
        const { data } = await axios.get(
          `${apiUrl}/api/payments/checkout/status/${sessionId}`,
        );
        if (cancelled) return;
        setDetails(data);

        if (data.payment_status === 'paid') {
          setStatus('paid');
          return;
        }
        if (data.status === 'expired' || data.payment_status === 'failed') {
          setStatus('failed');
          return;
        }

        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setStatus('timeout');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setStatus('timeout');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const renderBody = () => {
    if (status === 'missing') {
      return (
        <>
          <AlertTriangle className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
          <h1 className="text-3xl font-headline font-bold mb-3">No session found</h1>
          <p className="text-[#A1A1AA] font-body">
            We couldn't find a payment session in the URL. Head back to pricing and try again.
          </p>
        </>
      );
    }
    if (status === 'checking') {
      return (
        <>
          <Loader2 className="w-16 h-16 text-[#00E599] mx-auto mb-6 animate-spin" />
          <h1 className="text-3xl font-headline font-bold mb-3">Confirming your payment…</h1>
          <p className="text-[#A1A1AA] font-body">
            Hang tight — we're just double-checking with Stripe.
          </p>
        </>
      );
    }
    if (status === 'paid') {
      return (
        <>
          <div className="w-20 h-20 bg-[#00E599]/15 border border-[#00E599]/40 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-[#00E599]" />
          </div>
          <h1 className="text-3xl font-headline font-bold mb-3">Payment received. Welcome aboard.</h1>
          <p className="text-[#A1A1AA] font-body mb-6">
            We've queued up your AI Office Manager onboarding. Our team will reach out within 24 hours to kick off your 14-day build.
          </p>
          {details && (
            <div className="glass p-5 rounded-xl border border-[#00E599]/20 text-left mb-8" data-testid="payment-details">
              <div className="text-xs text-[#A1A1AA] font-body mb-2">Receipt</div>
              <div className="flex justify-between text-sm text-white font-body">
                <span>Plan</span>
                <span className="font-semibold capitalize">{details.package_id}</span>
              </div>
              <div className="flex justify-between text-sm text-white font-body">
                <span>Amount</span>
                <span className="font-semibold">
                  ${(details.amount_total / 100).toLocaleString()} {details.currency?.toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between text-sm text-white font-body">
                <span>Session</span>
                <span className="font-mono text-xs text-[#A1A1AA]">{sessionId?.slice(0, 22)}…</span>
              </div>
            </div>
          )}
        </>
      );
    }
    if (status === 'failed') {
      return (
        <>
          <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-6" />
          <h1 className="text-3xl font-headline font-bold mb-3">Payment didn't go through</h1>
          <p className="text-[#A1A1AA] font-body">
            The session expired or was declined. You haven't been charged. Hop back to pricing to try again.
          </p>
        </>
      );
    }
    // timeout
    return (
      <>
        <AlertTriangle className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
        <h1 className="text-3xl font-headline font-bold mb-3">Still confirming…</h1>
        <p className="text-[#A1A1AA] font-body">
          Stripe is taking a little longer than usual. Check your email for a receipt — if it hasn't arrived in a few minutes, refresh this page.
        </p>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="glass p-10 rounded-2xl max-w-lg w-full border border-[#00E599]/20 text-center">
        {renderBody()}
        <Link to="/" data-testid="payment-back-home">
          <Button className="mt-6 bg-[#00E599] hover:bg-[#00C985] text-[#050505] font-bold">
            Back to Home
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default PaymentSuccess;
